import { useEffect, useState } from 'react';

/**
 * Hook to detect and sync system theme preference with HTML element
 * Listens to changes in prefers-color-scheme and updates the 'dark' class on HTML element
 */
export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'medobill-theme-preference';

const getStoredPreference = (): ThemePreference => {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
};

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const applyTheme = (theme: 'light' | 'dark') => {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme;
};

export const useTheme = () => {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(getStoredPreference);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const preference = getStoredPreference();
    const resolved = preference === 'system' ? getSystemTheme() : preference;
    return resolved === 'dark';
  });

  const resolvedTheme = themePreference === 'system' ? getSystemTheme() : themePreference;

  useEffect(() => {
    setIsDarkMode(resolvedTheme === 'dark');
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemChange = (event?: MediaQueryListEvent) => {
      if (themePreference !== 'system') return;
      const matches = event ? event.matches : mediaQuery.matches;
      setIsDarkMode(matches);
      applyTheme(matches ? 'dark' : 'light');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemChange);
      return () => mediaQuery.removeEventListener('change', handleSystemChange);
    }

    const listener = handleSystemChange as (this: MediaQueryList, ev?: MediaQueryListEvent) => void;
    mediaQuery.addListener(listener);
    return () => mediaQuery.removeListener(listener);
  }, [themePreference]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleExternalChange = () => {
      setThemePreferenceState(getStoredPreference());
    };

    window.addEventListener('storage', handleExternalChange);
    window.addEventListener('theme-change', handleExternalChange as EventListener);
    return () => {
      window.removeEventListener('storage', handleExternalChange);
      window.removeEventListener('theme-change', handleExternalChange as EventListener);
    };
  }, []);

  const setThemePreference = (preference: ThemePreference) => {
    setThemePreferenceState(preference);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, preference);
      window.dispatchEvent(new Event('theme-change'));
    }
  };

  const toggleTheme = () => {
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    setThemePreference(next);
  };

  return { isDarkMode, themePreference, setThemePreference, toggleTheme };
};

